/**
 * AiReviewTierPicker — single source of truth for tier selection UX.
 *
 * Reads the catalogue from /api/lesson-review/tiers (server-driven so
 * tier copy + defaults can be tweaked without redeploying the
 * frontend) and renders one card per tier with the role/context
 * default pre-selected.
 *
 * Usage:
 *   <AiReviewTierPicker
 *     context="self_review"   // "self_help" | "pupil_paid" | "self_review" | "examiner_prep"
 *     value={tier}
 *     onChange={setTier}
 *     testIdPrefix="rar"      // mounts data-testid={prefix}-tier-{id}
 *   />
 *
 * Keeps backwards-compat by gracefully falling back to a hard-coded
 * tier list if the API is unreachable. Pricing is intentionally
 * not displayed — kept flexible while real costs are still being
 * discovered (operator directive Feb 2026).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Zap, Layers, Award } from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

// Local fallback catalogue — mirrors backend ai_review_tiers.TIERS
// shape so a network failure doesn't block the picker.
const FALLBACK_TIERS = [
  { id: "quick", label: "Quick",
    description: "Fast top-line take — best for self-help moments." },
  { id: "full",  label: "Full",
    description: "Standard pupil-paid review with the full Part-3 grid." },
  { id: "deep",  label: "Deep",
    description: "ADI/PDI self-review with extended coaching narrative." },
  { id: "pro",   label: "Pro",
    description: "Examiner-style standards-check prep with full report." },
];

const TIER_ICONS = {
  quick: Zap,
  full:  Sparkles,
  deep:  Layers,
  pro:   Award,
};

export default function AiReviewTierPicker({
  context = "self_review",
  value,
  onChange,
  testIdPrefix = "tier",
  compact = false,
}) {
  const [catalogue, setCatalogue] = useState(null);
  const [serverDefault, setServerDefault] = useState(null);

  useEffect(() => {
    if (!MEDIA_FEATURE_ENABLED) return;       // Media quarantine — no fetch.
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/lesson-review/tiers`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setCatalogue(r.data?.tiers || null);
        setServerDefault(r.data?.defaults?.[context] || null);
      } catch {
        // fall back silently — picker still usable
      }
    })();
    return () => { cancelled = true; };
  }, [context]);

  const tiers = catalogue || FALLBACK_TIERS;

  // Apply server default when the parent hasn't set a value yet.
  useEffect(() => {
    if (value || !serverDefault) return;
    onChange?.(serverDefault);
  }, [serverDefault, value, onChange]);

  const selected = value || serverDefault || "full";

  const list = useMemo(() => tiers, [tiers]);

  // Media quarantine — silent. No CTA. Mirrors LatestReviewCard.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-tier-picker`}
      data-tier-context={context}
      data-tier-default={serverDefault || ""}
      className="space-y-2"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">
          AI review depth
        </p>
        <p className="text-[10px] text-slate-500">
          Suggested for you · <span className="text-indigo-300 font-bold">
            {(tiers.find((t) => t.id === serverDefault) || {}).label || "Full"}
          </span>
        </p>
      </div>
      <div className={compact ? "grid grid-cols-2 gap-1.5" : "space-y-1.5"}>
        {list.map((t) => {
          const Icon = TIER_ICONS[t.id] || Sparkles;
          const isSelected = selected === t.id;
          const isDefault = serverDefault === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`${testIdPrefix}-tier-${t.id}`}
              data-tier-selected={isSelected ? "true" : "false"}
              onClick={() => onChange?.(t.id)}
              className={`w-full rounded-lg ${compact ? "p-2.5" : "p-3"} ring-2 transition text-left flex items-start gap-3 ${
                isSelected
                  ? "ring-indigo-500 bg-indigo-500/15"
                  : "ring-transparent bg-slate-800 hover:bg-slate-700"
              }`}
            >
              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                isSelected ? "text-indigo-300" : "text-slate-400"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-extrabold text-white">
                    {t.label}
                  </span>
                  {isDefault && (
                    <span className="text-[9px] uppercase tracking-widest font-extrabold text-indigo-300 bg-indigo-500/20 px-1.5 py-0.5 rounded">
                      Default
                    </span>
                  )}
                </div>
                {!compact && (
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {t.description}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Tiny presentational helper for tier badges on queue cards / result
// summaries. Decoupled from the picker so any surface can show "Tier:
// Deep" without the full radio UI.
export function AiReviewTierBadge({ tier, className = "" }) {
  if (!tier) return null;
  const cfg = FALLBACK_TIERS.find((t) => t.id === tier) ||
              { id: tier, label: tier };
  const Icon = TIER_ICONS[cfg.id] || Sparkles;
  return (
    <span
      data-testid={`tier-badge-${cfg.id}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-extrabold text-indigo-300 bg-indigo-500/15 ring-1 ring-indigo-500/30 px-1.5 py-0.5 rounded ${className}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}
